import React from 'react'
import { View } from 'react-native'

import BridgeIcon from '@common/assets/svg/BridgeIcon'
import DAppsIcon from '@common/assets/svg/DAppsIcon'
import ReceiveIcon from '@common/assets/svg/ReceiveIcon'
import SendIcon from '@common/assets/svg/SendIcon'
import SwapAndBridgeIcon from '@common/assets/svg/SwapAndBridgeIcon'
import ShieldTickIcon from '@common/assets/svg/ShieldTickIcon'
import ActivityIcon from '@common/assets/svg/ActivityIcon'
import { isMobileWebApp } from '@common/config/env'
import { useTranslation } from '@common/config/localization'
import { useReceiveSheet } from '@common/contexts/receiveSheetContext'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import useAppMode from '@web/hooks/useAppMode'
import flexbox from '@common/styles/utils/flexbox'
import GlobeIcon from '@common/assets/svg/GlobeIcon'
import { ModeEnum } from '@web/contexts/appModeContext'
import {
  isPrivateModeBlocked,
  PRIVATE_MODE_TOOLTIP,
  PrivateModeReason
} from '@web/utils/privateModeAvailability'

import RouteItem from './RouteItem'
import { RouteItemType } from './RouteItem/RouteItem'

type Props = {
  privateModeReason: PrivateModeReason
}

// Fast shield/unshield sign, then POST the signature to the relayer from the
// screen itself — and the browser-action popup is killed the moment the Sign
// window takes focus, so that second half would never run (Trello #376). Until
// those flows move into a background controller (as gasless/CCTP swap already
// did), open them in the full-page tab. The screens also bounce out of the popup
// on their own; this just avoids the flash.

const Routes = ({ privateModeReason }: Props) => {
  const { t } = useTranslation()
  const { openReceive } = useReceiveSheet()
  const { mode } = useAppMode()
  const isShieldBlocked = isPrivateModeBlocked(privateModeReason)
  const hiddenRoutes =
    mode === ModeEnum.PUBLIC
      ? [WEB_ROUTES.transferPrivate, WEB_ROUTES.unshield, WEB_ROUTES.activity]
      : // Swap & Bridge stays hidden in PRIVATE mode: it swaps PUBLIC on-chain funds, not
        // RAILGUN shielded balances, so showing it here would let users swap public funds while
        // the dashboard shows their shield. Private swap needs an unshield→swap→reshield flow
        // that does not exist yet.
        [
          WEB_ROUTES.transfer,
          WEB_ROUTES.shield,
          WEB_ROUTES.activity,
          WEB_ROUTES.swapAndBridge,
          WEB_ROUTES.swap,
          WEB_ROUTES.bridge
        ]

  const routeItems: RouteItemType[] = [
    {
      testID: 'dashboard-button-send',
      icon: SendIcon,
      label: t('Send'),
      route: WEB_ROUTES.transfer,
      scale: 1.08,
      scaleOnHover: 1.18
    },

    {
      testID: 'dashboard-button-send-private',
      icon: SendIcon,
      label: t('Send'),
      route: WEB_ROUTES.transferPrivate,
      scale: 1.08,
      scaleOnHover: 1.18
    },
    // Swap and Bridge are two separate shortcuts: same-chain swaps go to `/swap`
    // (ZKX Trade, plus LibertySwap on PulseChain) and cross-chain to `/bridge`.
    // `/swap-and-bridge` still resolves, for deep links made before the split.
    {
      testID: 'dashboard-button-swap',
      icon: SwapAndBridgeIcon,
      label: t('Swap'),
      route: WEB_ROUTES.swap,
      scale: 0.95,
      scaleOnHover: 1
    },
    {
      testID: 'dashboard-button-bridge',
      icon: BridgeIcon,
      label: t('Bridge'),
      route: WEB_ROUTES.bridge,
      scale: 0.95,
      scaleOnHover: 1
    },
    {
      testID: 'dashboard-button-receive',
      icon: ReceiveIcon,
      label: t('Receive'),
      route: WEB_ROUTES.receive,
      // The receive modal (ReceiveSheetContent) reads state via the in-process
      // controller store, which only exists in the mobile-web build. In the
      // extension popup that store isn't mounted, so open the dedicated receive
      // route screen instead of the overlay (which would crash and render empty).
      onPress: isMobileWebApp ? () => openReceive() : undefined,
      scale: 1.08,
      scaleOnHover: 1.18
    },
    {
      testID: 'dashboard-button-unshield',
      icon: GlobeIcon,
      label: t('Unshield'),
      route: WEB_ROUTES.unshield,
      scale: 1.08,
      scaleOnHover: 1.18
    },
    {
      testID: 'dashboard-button-shield',
      icon: ShieldTickIcon,
      label: t('Shield'),
      route: WEB_ROUTES.shield,
      scale: 1.08,
      scaleOnHover: 1.18,
      // Disable ONLY when the account can never go private (view-only / hardware
      // / raw private-key — even with a stale wallet row). A missing Railgun
      // wallet is no longer a reason to disable: the engine is lazy now (it boots
      // on the Private toggle or on entering a private screen), so gating on
      // `hasRailgunWallet` here left the tile permanently disabled for accounts
      // that never toggled Private. Shield itself needs no engine to open (the
      // recipient 0zk address is derived without one) and boots it lazily once
      // the form is valid — see the deferred-boot effect in ShieldScreenBody.
      disabled: isShieldBlocked,
      disabledTooltip:
        isShieldBlocked && privateModeReason !== 'available'
          ? {
              id: `dashboard-shield-${privateModeReason}`,
              content: t(PRIVATE_MODE_TOOLTIP[privateModeReason])
            }
          : undefined
    },
    {
      testID: 'dashboard-button-activity',
      icon: ActivityIcon,
      label: t('Activity'),
      route: WEB_ROUTES.activity,
      scale: 1.08,
      scaleOnHover: 1.18
    },
    {
      testID: 'dashboard-button-apps',
      icon: DAppsIcon,
      label: t('Apps'),
      route: WEB_ROUTES.apps,
      scale: 0.95,
      scaleOnHover: 1.02
    }
  ]

  const filteredRouteItems = routeItems.filter(
    (routeItem) => !hiddenRoutes.includes((routeItem.filterKey ?? routeItem.route) as string)
  )

  return (
    <View style={[flexbox.directionRow]}>
      {filteredRouteItems.map((routeItem, index) => (
        <RouteItem
          key={routeItem.label}
          routeItem={routeItem}
          index={index}
          routeItemsLength={routeItems.length}
        />
      ))}
    </View>
  )
}

export default React.memo(Routes)
